import { useCallback, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import { captureCanvas } from '@utils/canvasSnapshot';
import { uploadGameImage, buildSubmissionPath } from '@utils/imageStorage';
import { calculateScores, detectTie, getVotingMode } from '@utils/scoreCalculator';
import challengePacks from '@/data/challengePacks.json';

/**
 * Hook providing all Challenge Mode game actions.
 */
export function useChallenge() {
  const { state, dispatch } = useApp();
  const firebase = useFirebase();
  const { user, room, game } = state;
  const usedChallengesRef = useRef(new Set());

  const memberCount = Object.keys(room.members || {}).length;
  const votingMode = getVotingMode(memberCount);

  // Pick a random challenge from a pack, avoiding repeats
  const pickChallenge = useCallback((packId) => {
    const pack = challengePacks.packs.find(p => p.id === packId);
    if (!pack) return null;
    const available = pack.challenges.filter(c => !usedChallengesRef.current.has(c));
    const pool = available.length > 0 ? available : pack.challenges;
    const text = pool[Math.floor(Math.random() * pool.length)];
    usedChallengesRef.current.add(text);
    return { text, category: pack.name, packId: pack.id };
  }, []);

  // Host: initialise challenge lobby with a selected pack
  const initChallenge = useCallback(async (packId) => {
    const challenge = pickChallenge(packId);
    if (!challenge) return;
    await firebase.updateGameState({
      challenge,
      submissions: null,
      votes: null,
      scores: null,
      drawoffEligible: null,
      winner: null,
    });
  }, [firebase, pickChallenge]);

  // Host: reroll to a different challenge from the same pack
  const rerollChallenge = useCallback(async () => {
    if (!game.challenge?.packId) return;
    const challenge = pickChallenge(game.challenge.packId);
    if (challenge) {
      await firebase.updateGameState({ challenge });
    }
  }, [firebase, game.challenge, pickChallenge]);

  // Host: start the drawing phase
  const startDrawing = useCallback(async (timerDuration) => {
    await firebase.updateGameState({
      phase: 'drawing',
      timerDuration,
      timerEndsAt: Date.now() + (timerDuration * 1000),
    });
  }, [firebase]);

  // Host: attach an art style to the current challenge (optional)
  const setChallengeStyle = useCallback(async (style) => {
    if (!style) {
      // Clear the style
      await firebase.updateGameState({
        'challenge/style': null,
      });
      return;
    }
    await firebase.updateGameState({
      'challenge/style': {
        id: style.id,
        name: style.name,
        emoji: style.emoji,
        colour: style.colour,
        toolPreset: style.toolPreset,
        techniqueTips: style.techniqueTips,
      },
    });
  }, [firebase]);

  // Player: submit their canvas — upload to Storage, write URL to RTDB
  const submitEntry = useCallback(async (stageRef) => {
    const dataUrl = captureCanvas(stageRef);
    if (!dataUrl) return;
    const path = buildSubmissionPath(state.room.code, 'challenge', user.id);
    const imageUrl = await uploadGameImage(dataUrl, path);
    if (!imageUrl) return;
    await firebase.submitChallengeEntry(user.id, imageUrl, user.nickname);
  }, [firebase, user.id, user.nickname, state.room.code]);

  // Host: auto-submit and advance to review when timer expires
  const advanceToReview = useCallback(async () => {
    await firebase.updateGameState({
      phase: 'review',
      timerEndsAt: null,
    });
  }, [firebase]);

  // Host: advance from review to voting
  const advanceToVoting = useCallback(async () => {
    await firebase.updateGameState({
      phase: 'voting',
      timerEndsAt: null,
    });
  }, [firebase]);

  // Player: submit a single vote
  const submitVoteForTarget = useCallback(async (targetUserId, score) => {
    await firebase.submitVote(user.id, targetUserId, score);
  }, [firebase, user.id]);

  // Player: mark all votes as done
  const finishVoting = useCallback(async () => {
    await firebase.markVotingComplete(user.id);
  }, [firebase, user.id]);

  // Host: calculate results and advance
  const calculateResults = useCallback(async () => {
    const scores = calculateScores(game.votes, game.submissions, votingMode);
    const tied = detectTie(scores);

    const updates = {
      phase: 'results',
      scores,
    };

    if (tied) {
      updates.drawoffEligible = tied;
      updates.winner = null;
    } else {
      const sorted = Object.entries(scores).sort(([, a], [, b]) => b - a);
      updates.winner = sorted[0]?.[0] || null;
      updates.drawoffEligible = null;
    }

    await firebase.updateGameState(updates);
  }, [firebase, game.votes, game.submissions, votingMode]);

  // Host: start a draw-off
  const startDrawOff = useCallback(async (challengeText) => {
    await firebase.updateGameState({
      phase: 'drawoff',
      submissions: null,
      votes: null,
      timerDuration: 60,
      timerEndsAt: Date.now() + 60000,
      challenge: challengeText ? { ...game.challenge, text: challengeText } : game.challenge,
    });
  }, [firebase, game.challenge]);

  // Host: play again with fresh challenge
  const playAgain = useCallback(async () => {
    usedChallengesRef.current.clear();
    await firebase.updateGameState({
      phase: 'lobby',
      round: (game.round || 1) + 1,
      submissions: null,
      votes: null,
      scores: null,
      drawoffEligible: null,
      winner: null,
      timerEndsAt: null,
      challenge: null,
    });
  }, [firebase, game.round]);

  // Host: end the game entirely
  const endGame = useCallback(async () => {
    if (state.canvas.savedSharedStrokes) {
      dispatch({ type: 'RESTORE_SHARED_STROKES' });
    }
    await firebase.clearGame();
    dispatch({ type: 'CLEAR_GAME' });
    dispatch({ type: 'ADD_TOAST', payload: { message: 'Game ended — back to free draw' } });
  }, [firebase, dispatch, state.canvas.savedSharedStrokes]);

  return {
    // State
    votingMode,
    memberCount,
    // Actions
    initChallenge,
    setChallengeStyle,
    rerollChallenge,
    pickChallenge,
    startDrawing,
    submitEntry,
    advanceToReview,
    advanceToVoting,
    submitVoteForTarget,
    finishVoting,
    calculateResults,
    startDrawOff,
    playAgain,
    endGame,
  };
}
